import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  type FileRevision,
  FileRevisionSchema,
  type FileRevisions,
  type FileRevisionsQuery,
  FileRevisionsSchema,
  type FileRevisionsSearchOptions,
  type FileRevisionsSearchResults,
  FileRevisionsSearchResultsSchema,
  type GetSignedDownloadUrlResponse,
  GetSignedDownloadUrlResponseSchema,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const useFileRevisionsQuery = (
  params: FileRevisionsQuery,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['file-revisions', params],
    queryFn: async () => {
      const response = await apiClient.get<FileRevisions>('/file-revisions', {
        params,
      });
      const validatedData = FileRevisionsSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate file revisions data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

export const useFileRevisionQuery = (
  fileRevisionId: string | undefined | null,
) => {
  return useQuery({
    queryKey: ['file-revision', fileRevisionId],
    queryFn: async () => {
      const response = await apiClient.get<FileRevision>(
        `/file-revisions/${fileRevisionId}`,
      );
      const validatedData = FileRevisionSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate file revision', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled: !!fileRevisionId,
  });
};

export const useFileRevisionDownloadUrlQuery = (
  fileRevisionId: string | null | undefined,
) => {
  return useQuery({
    queryKey: ['file-revision-download-url', fileRevisionId],
    queryFn: async () => {
      const response = await apiClient.get<GetSignedDownloadUrlResponse>(
        `/file-revisions/${fileRevisionId}/downloadUrl`,
      );
      const validatedData = GetSignedDownloadUrlResponseSchema.safeParse(
        response.data,
      );
      if (!validatedData.success) {
        logger.warn('failed to validate file revision download url', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled: !!fileRevisionId,
  });
};

export const useFileRevisionsSearchQuery = (
  options: FileRevisionsSearchOptions,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['file-revisions-search', options],
    queryFn: async () => {
      const response = await apiClient.get<FileRevisionsSearchResults>(
        '/file-revisions/search',
        { params: options },
      );
      const validatedData = FileRevisionsSearchResultsSchema.safeParse(
        response.data,
      );
      if (!validatedData.success) {
        logger.warn('failed to validate file revisions search results', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};
